
import { useState } from 'react';
import { X, Flag, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useContentReports, type ReportReason, type ContentType } from '@/hooks/useContentReports';

interface ReportContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  contentType: ContentType;
  contentPreview?: string;
}

const reportReasons: { value: ReportReason; label: string; description: string }[] = [
  {
    value: 'spam',
    label: 'Spam',
    description: 'Repetitive, unwanted, or promotional content'
  },
  {
    value: 'harassment',
    label: 'Harassment or Bullying',
    description: 'Content that targets or intimidates individuals'
  },
  {
    value: 'inappropriate_content',
    label: 'Inappropriate Content',
    description: 'Content not suitable for the community'
  },
  {
    value: 'misinformation',
    label: 'Misinformation',
    description: 'False or misleading information'
  },
  {
    value: 'hate_speech',
    label: 'Hate Speech',
    description: 'Content that promotes hatred or discrimination'
  },
  {
    value: 'violence',
    label: 'Violence or Threats',
    description: 'Content promoting or threatening violence'
  },
  {
    value: 'copyright_violation',
    label: 'Copyright Violation',
    description: 'Unauthorized use of copyrighted material'
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Another reason not listed above'
  }
];

export const ReportContentModal = ({
  isOpen,
  onClose,
  contentId,
  contentType,
  contentPreview
}: ReportContentModalProps) => {
  const [selectedReason, setSelectedReason] = useState<ReportReason | ''>('');
  const [description, setDescription] = useState('');
  const { submitReport, isSubmitting } = useContentReports();

  const handleSubmit = async () => {
    if (!selectedReason) return;

    const success = await submitReport(
      contentId,
      contentType,
      selectedReason,
      description.trim() || undefined
    );

    if (success) {
      onClose();
      setSelectedReason('');
      setDescription('');
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedReason('');
    setDescription('');
  };

  const handleReasonChange = (value: string) => {
    setSelectedReason(value as ReportReason);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" aria-describedby="report-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="text-red-500" size={20} />
            Report Content
          </DialogTitle>
          <DialogDescription>
            Help us keep the community safe by reporting inappropriate content.
          </DialogDescription>
        </DialogHeader>

        <p id="report-desc" className="sr-only">
          Report inappropriate content to help keep the YieldSocial farming community safe. Select a reason and provide additional details if needed.
        </p>

        <div className="space-y-4">
          {/* Content Preview */}
          {contentPreview && (
            <div className="p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm text-gray-600 mb-1">Reporting this {contentType}:</p>
              <p className="text-sm line-clamp-3">{contentPreview}</p>
            </div>
          )}

          {/* Report Reasons */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Why are you reporting this content?</Label>
            <RadioGroup value={selectedReason} onValueChange={handleReasonChange}>
              {reportReasons.map((reason) => (
                <div key={reason.value} className="flex items-start space-x-2">
                  <RadioGroupItem value={reason.value} id={reason.value} className="mt-1" />
                  <div className="flex-1">
                    <Label 
                      htmlFor={reason.value} 
                      className="text-sm font-medium cursor-pointer"
                    >
                      {reason.label}
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">{reason.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Additional Details */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Additional details (optional)
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide any additional context about why you're reporting this content..."
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-gray-500 text-right">
              {description.length}/500 characters
            </p>
          </div>

          {/* Warning Notice */}
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="text-yellow-600 flex-shrink-0" size={16} />
            <p className="text-xs text-yellow-800">
              False reports may result in restrictions on your account. Please only report content that violates our community guidelines.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedReason || isSubmitting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
